import { FormControl, FormGroup } from "@angular/forms";
import {
  BmAnalyticsBarData,
  BmAnalyticsDataFg, BmAnalyticsPieData,
  BmFundAnalyticsFg,
  BmModelDataFg,
  BudgetModelFormGroup
} from "../../../pojo/bm-form";
import {DateService, parseYearsPassed} from "../../../../../../util/common/date.service";
import {MAX_MODEL_YEARS_NUMBER} from "../../../pojo/bm-const";
import {BmCalcHelperService} from "../../../services/general/bm-calc-helper.service";
import {BmAnalyticsData, BmFundActualAnalytics, BmPeriodEnum} from "../../../pojo/bm-classes";
import {LocalDate} from "../../../../../../core/component/pojo/date-variable";
import {BmAnalyticsBarChartsData} from "../../../pojo/bm-analytics-classes";


export class BmAnalyticsCalculationsService {

  public static updateAnalyticsForm(bmForm: BudgetModelFormGroup): void {
    const analyticsFg = bmForm.controls.analyticsData;
    const modelData = bmForm.controls.modelData;

    this.fillBudgetPeriodDataFg(analyticsFg.controls.periodBudgetData, modelData);
    this.fillBarChartsDataBudgetOnly(analyticsFg, modelData);
    this.fillPieChartFromPeriodData(analyticsFg.controls.pieCharts, analyticsFg.controls.periodBudgetData);
  }

  public static fillBudgetPeriodDataFg(budgetDataForm: FormGroup<BmFundAnalyticsFg>, modelData: FormGroup<BmModelDataFg>): void {
    if (!budgetDataForm || !modelData) {
      return;
    }
    // if initial closing not selected => year passed is null => MAX_MODEL_YEARS_NUMBER should be used
    const { years, quarters } = parseYearsPassed(MAX_MODEL_YEARS_NUMBER);

    const opExp = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.operatingExpenses.controls.total, years, quarters);
    const orgExp = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.organizationalExpenses.controls.total, years, quarters);
    const investment = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.investment.controls.itemsValue, years, quarters);
    const mgmFee = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.mgmFee.controls.total, years, quarters);
    const totalExp = opExp + orgExp + mgmFee;
    const capRequired = totalExp + investment;

    budgetDataForm.patchValue({
      capRequired: capRequired,
      totalExp: totalExp,
      investment: investment,
      mgmFee: mgmFee,
      opExp: opExp,
      orgExp: orgExp,
    }, { emitEvent: false });
  }

  private static fillBarChartsDataBudgetOnly(form: FormGroup<BmAnalyticsDataFg>, modelData: FormGroup<BmModelDataFg>): void {
    const budgetData = this.calculateAllBudgetDataSimple(modelData);

    this.fillSingleBarChart(form.controls.barCharts.controls.capRequired, budgetData.capRequired);
    this.fillSingleBarChart(form.controls.barCharts.controls.totalExp, budgetData.totalExp);
    this.fillSingleBarChart(form.controls.barCharts.controls.investment, budgetData.investment);
    this.fillSingleBarChart(form.controls.barCharts.controls.mgmFee, budgetData.mgmFee);
    this.fillSingleBarChart(form.controls.barCharts.controls.opExp, budgetData.opExp);
    this.fillSingleBarChart(form.controls.barCharts.controls.orgExp, budgetData.orgExp);
  }


  private static calculateAllBudgetDataSimple(modelData: FormGroup<BmModelDataFg>): BmAnalyticsBarChartsData {

    // Get all dateDto from model data
    const dateDto: { year: number, quarter: number, chartXValue: number }[] = [];

    modelData.controls.mgmFee.controls.total.controls.forEach((item) => {
      const periodValue = item.value.period;
      if (periodValue.type === BmPeriodEnum.QUARTER) {
        dateDto.push({
          year: periodValue.yearNumber,
          quarter: periodValue.quarterNumber,
          chartXValue: periodValue.yearNumber + (periodValue.quarterNumber - 1) * 0.25,
        });
      }
    });

    dateDto.sort((a, b) => a.chartXValue - b.chartXValue);

    const budgetData: BmAnalyticsBarChartsData = {
      capRequired: [],
      totalExp: [],
      investment: [],
      mgmFee: [],
      opExp: [],
      orgExp: [],
    };

    // Calculate all values once for each quarter
    dateDto.forEach(({ year, quarter, chartXValue }) => {
      const mgmFeeValue = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.mgmFee.controls.total, year, quarter);
      const mgmFeePcnt = modelData.value.mgmFee.totalRow ? mgmFeeValue / modelData.value.mgmFee.totalRow * 100 : null;
      const investmentValue = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.investment.controls.itemsValue, year, quarter);
      const investmentPcnt = modelData.value.investment.totalRow ? investmentValue / modelData.value.investment.totalRow * 100 : null;
      const opExpValue = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.operatingExpenses.controls.total, year, quarter);
      const opExpPcnt = modelData.value.operatingExpenses.totalRow ? opExpValue / modelData.value.operatingExpenses.totalRow * 100 : null;
      const orgExpValue = BmCalcHelperService.calculateAccumulatedValue(modelData.controls.organizationalExpenses.controls.total, year, quarter);
      const orgExpPcnt = modelData.value.organizationalExpenses.totalRow ? orgExpValue / modelData.value.organizationalExpenses.totalRow * 100 : null;
      const totalExpValue = opExpValue + orgExpValue + mgmFeeValue;
      const totalExpPcnt = modelData.value.totalExpenses.totalRow ? totalExpValue / modelData.value.totalExpenses.totalRow * 100 : null;
      const capRequiredValue = totalExpValue + investmentValue;
      const capRequiredPcnt = modelData.value.totalCapRequired.totalRow ? capRequiredValue / modelData.value.totalCapRequired.totalRow * 100 : null;

      budgetData.mgmFee.push([chartXValue, mgmFeeValue, mgmFeePcnt]);
      budgetData.investment.push([chartXValue, investmentValue, investmentPcnt]);
      budgetData.opExp.push([chartXValue, opExpValue, opExpPcnt]);
      budgetData.orgExp.push([chartXValue, orgExpValue, orgExpPcnt]);
      budgetData.totalExp.push([chartXValue, totalExpValue, totalExpPcnt]);
      budgetData.capRequired.push([chartXValue, capRequiredValue, capRequiredPcnt]);
    });

    return budgetData;
  }


  private static fillSingleBarChart(barDataForm: FormGroup<BmAnalyticsBarData>, budgetData: [number, number, number][]): void {
    const budgetArray = barDataForm.controls.budget;
    budgetArray.clear({ emitEvent: false });

    budgetData.forEach(([date, value, pcnt]) => {
      budgetArray.push(new FormControl<[number, number, number]>([date, value, pcnt]), { emitEvent: false });
    });
  }

  private static fillPieChartFromPeriodData(pieDataForm: FormGroup<BmAnalyticsPieData>, periodDataForm: FormGroup<BmFundAnalyticsFg>): void {
    const seriesArray = pieDataForm.controls.series;
    seriesArray.clear({ emitEvent: false });
    const pieData = [
      { y: periodDataForm.getRawValue().investment, name: 'Investment' },
      { y: periodDataForm.getRawValue().mgmFee, name: 'Management fees' },
      { y: periodDataForm.getRawValue().opExp, name: 'Operating expenses' },
      { y: periodDataForm.getRawValue().orgExp, name: 'Organizational expenses' },
    ].filter((item) => item.y !== 0);

    pieData.forEach((data) => {
      seriesArray.push(new FormControl<{y: number, name: string}>(data), { emitEvent: false });
    });
  }
}
